import React, { useState } from 'react';
import { ShoppingCartIcon, ListIcon, LayoutGridIcon, SettingsIcon, SearchIcon, FilterIcon, SlidersIcon, ChevronDownIcon } from 'lucide-react';
import { OrderList } from '../components/orders/OrderList';
import { OrderBoard } from '../components/orders/OrderBoard';
import { OrderDetail } from '../components/orders/OrderDetail';
import { ShopSwitcher } from '../components/orders/ShopSwitcher';
// Order status types
export type OrderStatus = 'new' | 'in-progress' | 'open' | 'completed' | 'cancelled';
// Order type definition
export interface Order {
  id: string;
  status: OrderStatus;
  paymentStatus: 'paid' | 'unpaid' | 'cancelled';
  customer: {
    name: string;
    email: string;
    address: string;
    mobileNumber: string;
    additionalPhone?: string;
    address2?: string;
  };
  items: Array<{
    name: string;
    quantity: number;
    price: number;
  }>;
  total: number;
  date: string;
  time: string;
  transaction?: string;
  transactionStatus?: 'confirmed' | 'pending' | 'failed';
  notes?: string;
}
// Mock shop data
const shops = [{
  id: '1',
  name: 'Beach Shop',
  logo: '•',
  color: '#f8d7d7',
  newOrders: 0
}, {
  id: '2',
  name: 'Island Shop',
  logo: '+',
  color: '#f8d7d7',
  newOrders: 0
}, {
  id: '3',
  name: 'ABC',
  logo: 'A',
  color: '#f8d7d7',
  newOrders: 0
}, {
  id: '4',
  name: 'abc',
  logo: 'A',
  color: '#f8d7d7',
  newOrders: 0
}];
// Mock order data
const mockOrders: Order[] = [{
  id: '220629-1e753',
  status: 'new',
  paymentStatus: 'paid',
  customer: {
    name: 'randy',
    email: 'randy@randy.com',
    address: 'randy',
    mobileNumber: '6596605522',
    additionalPhone: '1234',
    address2: '1234'
  },
  items: [{
    name: 'Kittens',
    quantity: 2,
    price: 111.0
  }],
  total: 222.0,
  date: '29/06/2022',
  time: '14:28',
  transaction: '62bc1b4be3c2250009f1e753',
  transactionStatus: 'confirmed'
}, {
  id: '210505-00003',
  status: 'new',
  paymentStatus: 'cancelled',
  customer: {
    name: 'SOWMIYA',
    email: 'sowme76@gmail.com',
    address: 'ADDESS1',
    mobileNumber: '6598661234'
  },
  items: [{
    name: 'Kittens',
    quantity: 3,
    price: 110.0
  }],
  total: 330.0,
  date: '05/05/2021',
  time: '07:52'
}, {
  id: '210506-00004',
  status: 'in-progress',
  paymentStatus: 'paid',
  customer: {
    name: 'sowmiya',
    email: 'sowme76@gmail.com',
    address: 'Address Line 1',
    mobileNumber: '6598661234'
  },
  items: [{
    name: 'Kittens',
    quantity: 2,
    price: 111.0
  }],
  total: 222.0,
  date: '06/05/2021',
  time: '12:48'
}, {
  id: '210504-00002',
  status: 'in-progress',
  paymentStatus: 'cancelled',
  customer: {
    name: 'sowmiya',
    email: 'sowmiya@pomel.com',
    address: 'Address Line 1',
    mobileNumber: '6598661234'
  },
  items: [{
    name: 'Kittens',
    quantity: 2,
    price: 111.0
  }],
  total: 222.0,
  date: '04/05/2021',
  time: '16:06'
}, {
  id: '210503-00001',
  status: 'completed',
  paymentStatus: 'paid',
  customer: {
    name: 'John Doe',
    email: 'john@example.com',
    address: 'Male, Maldives',
    mobileNumber: '9876543210'
  },
  items: [{
    name: 'Kittens',
    quantity: 1,
    price: 111.0
  }],
  total: 111.0,
  date: '03/05/2021',
  time: '10:30'
}, {
  id: '210502-00005',
  status: 'completed',
  paymentStatus: 'paid',
  customer: {
    name: 'Jane Smith',
    email: 'jane@example.com',
    address: 'Male, Maldives',
    mobileNumber: '9876543211'
  },
  items: [{
    name: 'Kittens',
    quantity: 1,
    price: 111.0
  }],
  total: 111.0,
  date: '02/05/2021',
  time: '11:15'
}, {
  id: '210501-00006',
  status: 'completed',
  paymentStatus: 'paid',
  customer: {
    name: 'Alex Brown',
    email: 'alex@example.com',
    address: 'Male, Maldives',
    mobileNumber: '9876543212'
  },
  items: [{
    name: 'Kittens',
    quantity: 2,
    price: 111.0
  }],
  total: 222.0,
  date: '01/05/2021',
  time: '09:45'
}];
export function Orders() {
  const [orders, setOrders] = useState<Order[]>(mockOrders);
  const [selectedOrder, setSelectedOrder] = useState<Order | null>(null);
  const [viewMode, setViewMode] = useState<'list' | 'board'>('list');
  const [showShopSwitcher, setShowShopSwitcher] = useState(false);
  const [selectedShop, setSelectedShop] = useState(shops[0]);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState<OrderStatus | 'all'>('all');
  // Navigate to settings page with order-settings tab
  const navigateToOrderSettings = () => {
    window.location.href = '/settings?tab=order-settings';
  };
  // Order status definitions
  const orderStatuses = [{
    id: 'new',
    name: 'New',
    description: 'New orders'
  }, {
    id: 'in-progress',
    name: 'In progress',
    description: 'Orders which are being processed.'
  }, {
    id: 'open',
    name: 'OPEN',
    description: 'Open orders'
  }, {
    id: 'completed',
    name: 'Completed',
    description: 'Orders which have been completed.'
  }];
  // Filter orders based on search and status filter
  const filteredOrders = orders.filter(order => {
    const matchesSearch = order.id.toLowerCase().includes(searchTerm.toLowerCase()) || order.customer.name.toLowerCase().includes(searchTerm.toLowerCase()) || order.customer.email.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesStatus = statusFilter === 'all' || order.status === statusFilter;
    return matchesSearch && matchesStatus;
  });
  // Group orders by status
  const groupedOrders = orderStatuses.reduce((acc, status) => {
    acc[status.id as OrderStatus] = filteredOrders.filter(order => order.status === status.id);
    return acc;
  }, {} as Record<OrderStatus, Order[]>);
  // Move order between statuses
  const moveOrder = (orderId: string, newStatus: OrderStatus) => {
    setOrders(orders.map(order => order.id === orderId ? {
      ...order,
      status: newStatus
    } : order));
  };
  // Handle order click to show details
  const handleOrderClick = (order: Order) => {
    setSelectedOrder(order);
  };
  // Close order details
  const closeOrderDetails = () => {
    setSelectedOrder(null);
  };
  // Count orders by status
  const orderCounts = orderStatuses.reduce((acc, status) => {
    acc[status.id as OrderStatus] = orders.filter(order => order.status === status.id).length;
    return acc;
  }, {} as Record<OrderStatus, number>);
  return <div className="space-y-6" data-id="element-4592">
      {/* Top bar with actions */}
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center space-y-4 sm:space-y-0" data-id="element-4593">
        <div className="flex items-center" data-id="element-4594">
          <h1 className="text-2xl font-bold text-gray-800 flex items-center" data-id="element-4595">
            <ShoppingCartIcon size={24} className="mr-2 text-blue-600" data-id="element-4596" />
            Orders
          </h1>
          <div className="ml-4 flex space-x-1" data-id="element-4597">
            <button onClick={() => setViewMode('list')} className={`p-1.5 rounded ${viewMode === 'list' ? 'bg-gray-200' : 'hover:bg-gray-100'}`} data-id="element-4598">
              <ListIcon size={20} className="text-gray-700" data-id="element-4599" />
            </button>
            <button onClick={() => setViewMode('board')} className={`p-1.5 rounded ${viewMode === 'board' ? 'bg-gray-200' : 'hover:bg-gray-100'}`} data-id="element-4600">
              <LayoutGridIcon size={20} className="text-gray-700" data-id="element-4601" />
            </button>
          </div>
        </div>
        <div className="flex items-center space-x-3" data-id="element-4602">
          <div className="relative" data-id="element-4603">
            <button onClick={() => setShowShopSwitcher(!showShopSwitcher)} className="flex items-center space-x-2 px-3 py-2 bg-white border border-gray-300 rounded-md hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-blue-500" data-id="element-4604">
              <div className="flex items-center" data-id="element-4605">
                <div className="w-6 h-6 rounded-full flex items-center justify-center mr-2" style={{
                backgroundColor: selectedShop.color
              }} data-id="element-4606">
                  <span className="text-red-600 text-xs" data-id="element-4607">
                    {selectedShop.logo}
                  </span>
                </div>
                <span className="text-sm font-medium" data-id="element-4608">{selectedShop.name}</span>
              </div>
              <ChevronDownIcon size={16} className="text-gray-500" data-id="element-4609" />
            </button>
            {showShopSwitcher && <ShopSwitcher shops={shops} selectedShop={selectedShop} onSelect={shop => {
            setSelectedShop(shop);
            setShowShopSwitcher(false);
          }} onClose={() => setShowShopSwitcher(false)} data-id="element-4610" />}
          </div>
          <button onClick={navigateToOrderSettings} className="p-2 bg-white border border-gray-300 rounded-md hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-blue-500" data-id="element-4611">
            <SettingsIcon size={20} className="text-gray-700" data-id="element-4612" />
          </button>
        </div>
      </div>
      {/* Search and filters */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4" data-id="element-4613">
        <div className="flex flex-col md:flex-row gap-4" data-id="element-4614">
          <div className="relative flex-grow" data-id="element-4615">
            <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none" data-id="element-4616">
              <SearchIcon size={16} className="text-gray-400" data-id="element-4617" />
            </div>
            <input type="text" className="block w-full pl-10 pr-3 py-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500" placeholder="Search orders by ID, customer name, or email..." value={searchTerm} onChange={e => setSearchTerm(e.target.value)} data-id="element-4618" />
          </div>
          <div className="flex space-x-4" data-id="element-4619">
            <div className="relative" data-id="element-4620">
              <select className="appearance-none bg-white border border-gray-300 rounded-md pl-3 pr-8 py-2 focus:ring-blue-500 focus:border-blue-500" value={statusFilter} onChange={e => setStatusFilter(e.target.value as OrderStatus | 'all')} data-id="element-4621">
                <option value="all" data-id="element-4622">All Statuses</option>
                {orderStatuses.map(status => <option key={status.id} value={status.id} data-id="element-4623">
                    {status.name}
                  </option>)}
              </select>
              <div className="absolute inset-y-0 right-0 pr-3 flex items-center pointer-events-none" data-id="element-4624">
                <FilterIcon size={16} className="text-gray-400" data-id="element-4625" />
              </div>
            </div>
            <button className="flex items-center px-3 py-2 border border-gray-300 rounded-md hover:bg-gray-50" data-id="element-4626">
              <SlidersIcon size={16} className="text-gray-500 mr-2" data-id="element-4627" />
              <span className="text-sm" data-id="element-4628">More Filters</span>
            </button>
          </div>
        </div>
      </div>
      {/* Order status tabs */}
      <div className="border-b border-gray-200" data-id="element-4629">
        <div className="flex space-x-8" data-id="element-4630">
          {orderStatuses.map(status => <button key={status.id} onClick={() => setStatusFilter(status.id as OrderStatus)} className={`pb-2 px-1 text-sm font-medium ${statusFilter === status.id ? 'text-blue-600 border-b-2 border-blue-600' : 'text-gray-500 hover:text-gray-700 hover:border-gray-300'}`} data-id="element-4631">
              {status.name} ({orderCounts[status.id as OrderStatus]})
            </button>)}
        </div>
      </div>
      {/* Main content area */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden" data-id="element-4632">
        {viewMode === 'list' ? <OrderList orders={filteredOrders} onOrderClick={handleOrderClick} data-id="element-4633" /> : <OrderBoard groupedOrders={groupedOrders} orderStatuses={orderStatuses} onOrderClick={handleOrderClick} onOrderMove={moveOrder} data-id="element-4634" />}
      </div>
      {/* Order detail sidebar */}
      {selectedOrder && <OrderDetail order={selectedOrder} onClose={closeOrderDetails} onStatusChange={newStatus => {
      moveOrder(selectedOrder.id, newStatus);
      setSelectedOrder({
        ...selectedOrder,
        status: newStatus
      });
    }} data-id="element-4635" />}
    </div>;
}